import { ReactNode, useCallback, useMemo, useState } from 'react';
import { OnChangeFn } from '@tanstack/react-table';
import { buildDocumentPickerColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentPickerColumns';
import { useMasterDocumentsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsQuery';
import { useMasterDocumentPreviewFile } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentPreviewFile.ts';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	DocumentWorkflowTemplateInfo,
	DocumentWorkflowTemplateVisibleIn,
} from '@dealstack/shared/lib/generated';

interface Props {
	selectedIds: string[];
	onSelectionChange: (ids: string[]) => void;
	renderRowEditor: (template: DocumentWorkflowTemplateInfo) => ReactNode;
}

/** Lists the programme's master documents with checkboxes; row expansion is delegated to `renderRowEditor`. */
export const DocumentPickerTable = ({ selectedIds, onSelectionChange, renderRowEditor }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	const { documentWorkflowTemplates, isLoading } = useMasterDocumentsQuery({});

	// Same rule ensureTemplatesAreSendable applies on the way in, so the picker cannot offer a
	// document the send would refuse: this flow is the non-investment one, and a document scoped
	// elsewhere also cannot be revoked afterwards from the Documents tab.
	const sendableTemplates = useMemo(
		() =>
			documentWorkflowTemplates.filter(
				(template) =>
					!template.archived &&
					template.visibleIn.includes(
						DocumentWorkflowTemplateVisibleIn.NON_INVESTMENT_WORKFLOWS
					)
			),
		[documentWorkflowTemplates]
	);

	// Reuses the Master Documents preview rather than a second viewer: same hook, same modal, so
	// a document previews identically from either surface.
	const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
	const closePreview = useCallback(() => setPreviewDocumentId(null), []);
	const previewDocument = useMemo(
		() => sendableTemplates.find((doc) => doc.id === previewDocumentId),
		[sendableTemplates, previewDocumentId]
	);
	const {
		file: previewFile,
		fileName: previewFileName,
		fileExtension: previewFileExtension,
		isLoading: isLoadingPreviewFile,
	} = useMasterDocumentPreviewFile({
		document: previewDocument,
		dealId,
		programmeId: mipId,
	});

	const columns = useMemo(
		() =>
			buildDocumentPickerColumns({
				tMipManage,
				tCommonUi,
				onPreview: setPreviewDocumentId,
			}),
		[tMipManage, tCommonUi]
	);

	// The whole list arrives in one request, so search, filters and paging all run over it here
	// rather than round-tripping. `false` keeps the state local: this is a modal, and writing
	// page/search into the URL would leak into the page behind it.
	const { mantineTableDataOperations } = useMantineTableWithPaginationFilteringSorting<
		DocumentWorkflowTemplateInfo,
		Record<string, never>
	>(columns, {}, false);

	const rowSelection = useMemo(
		() => Object.fromEntries(selectedIds.map((id) => [id, true])),
		[selectedIds]
	);

	const handleRowSelectionChange: OnChangeFn<Record<string, boolean>> = useCallback(
		(updater) => {
			const nextSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
			onSelectionChange(
				Object.entries(nextSelection)
					.filter(([, isSelected]) => isSelected)
					.map(([id]) => id)
			);
		},
		[rowSelection, onSelectionChange]
	);

	return (
		<>
			<MantineTable<DocumentWorkflowTemplateInfo>
				columns={columns}
				data={sendableTemplates}
				getRowId={(row) => row.id}
				isLoading={isLoading}
				enableRowSelection
				enableExpanding
				manualFiltering={false}
				// The modal is already near-fullscreen, and `.ReactModal__Content`'s centring
				// transform makes it the containing block for the table's `position: fixed`,
				// so going fullscreen moves the table out from under its own toolbar.
				enableFullScreenToggle={false}
				// MantineTable defaults manualSorting to true, which leaves getSortedRowModel
				// undefined: a header click moved the indicator and nothing else. The list is
				// fetched whole, so sorting belongs here alongside filtering and paging.
				manualSorting={false}
				dataOperations={mantineTableDataOperations}
				getRowCanExpand={() => true}
				renderDetailPanel={({ row }) => renderRowEditor(row.original)}
				// The table draws a separator on both sides of the gap — the row's own bottom border
				// and the detail panel's top border — which stack into one thick line. Drop the
				// panel's and let the row carry the single rule.
				mantineDetailPanelProps={{ style: { borderTop: 'none' } }}
				state={{ rowSelection }}
				onRowSelectionChange={handleRowSelectionChange}
				mantineTableBodyRowProps={({ row }) => ({
					id: row.original.id,
					'data-testid': `send-documents-row-${row.original.id}`,
					// Figma SDW/S3b: an expanded row is marked off from its editor with a dashed rule.
					...(row.getIsExpanded() && {
						style: { borderBottom: `1px dashed ${defaultTheme.v3.colors.gray[3]}` },
					}),
				})}
			/>
			<DocumentViewerModalV2
				opened={!!previewDocumentId}
				onClose={closePreview}
				file={previewFile}
				fileName={previewFileName}
				fileExtension={previewFileExtension}
				isLoading={isLoadingPreviewFile}
				showBackButton={false}
			/>
		</>
	);
};
